//LIBRARIES
import React, { Component } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { localize } from 'react-localize-redux';
import Auth from 'j-toker';
import Dialog from '@material-ui/core/Dialog';

//HELPERS
import { SyncLoader } from 'halogenium';
import { AccentColor } from 'helpers/AccentColorHelper';
import { onlyNumbers } from 'helpers/Formatter';
import { media } from 'helpers/MediaHelper';
import { getTranslatedString } from 'helpers/i18n';

//COMPONENTS
import PaysafeTenantConfirmation from './PaysafeTenantConfirmation';
import PaysafeSuccessfulPayment from './PaysafeSuccessfulPayment';
import PaysafeFailure from './PaysafeFailure';
import { PaysafeDirectDebitPaymentForm } from './PaysafeDirectDebitPaymentForm';

//ACTIONS
import { getTenantTodoList } from 'actions/todoActions';

const StyledDialogContainer = styled.div`
  padding: 0.5rem 2rem;
  ${media.mobile`
    overflow: scroll;
    height: 100%;
  `};
`;

const StyledLoadingContainer = styled.div`
  width: 100%;
  height: 100%;
  text-align: center;
  padding: 3rem 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StyledLoadingP = styled.p`
  margin-top: 2rem;
`;

const FINALIZING_STATE = 'finalizing';
const AUTHORIZING_STATE = 'authorizing';
const PAYMENT_FORM_STATE = 'payment_form';
const CONFIRMATION_STATE = 'confirmation';
const SUCCESS_STATE = 'success';
const FAILURE_STATE = 'failure';

class PaysafeDirectDebitPayment extends Component {
  state = {
    paymentDetails: {},
    paymentState: PAYMENT_FORM_STATE,
  };

  closeConfirmation = () => {
    this.setState({
      paymentState: PAYMENT_FORM_STATE,
    });
    this.props.toggleOpen();
  };

  authorizePaymentFormSubmit = async (values) => {
    const paymentDetails = {
      payment_amount: parseFloat(onlyNumbers(values.paymentAmount)).toFixed(2),
      account_holder_name: values.accountHolderName,
      transit_number: values.transitNumber,
      institution_number: values.institutionNumber,
      account_number: values.accountNumber,
      ach_account_type: values.accountType,
      routing_number: values.routingNumber,
      lease_application_id: this.props.leaseApplicationId || this.props.applicationId,
      payment_type: this.props.paymentType,
    };

    try {
      this.setState({ paymentState: AUTHORIZING_STATE });
      const response = await this.props.authorizePayment(paymentDetails);

      if (!response.value) {
        throw Error;
      }
      this.setState({
        paymentState: CONFIRMATION_STATE,
        paymentDetails: paymentDetails,
      });
    } catch (error) {
      this.setState({ paymentState: FAILURE_STATE });
      window.Rollbar.error('Failed to authorize direct debit payment');
    }
  };

  finalizePayment = async () => {
    this.setState({ paymentState: FINALIZING_STATE });

    const { paymentDetails } = this.state;
    let paymentDetailsPayload = {
      ...paymentDetails,
      payment_with_fee: this.props.paymentWithFee,
      application_id: this.props.applicationId,
      lease_application_id: this.props.leaseApplicationId,
      payment_type: this.props.paymentType,
    };

    try {
      const response = await this.props.makePayment(paymentDetailsPayload);
      if (!response.value) {
        throw Error;
      }
      this.setState({
        paymentState: SUCCESS_STATE,
      });
    } catch (error) {
      this.setState({ paymentState: FAILURE_STATE });
      window.Rollbar.error(
        'payments(paysafe): Failed to make direct debit payment in Resident Portal',
        {
          error: error,
          ...paymentDetailsPayload,
          user_id: Auth.user.id,
          user_email: Auth.user.email,
        }
      );
    }
  };

  closePaymentSuccess = () => {
    this.props.getTenantTodoList();
    this.props.toggleOpen();
    this.props.onSuccess();
    this.setState({
      paymentState: PAYMENT_FORM_STATE,
      paymentDetails: {},
    });
  };

  closePaymentFailure = () => {
    this.setState({
      paymentState: PAYMENT_FORM_STATE,
    });
  };

  render() {
    const { paymentState } = this.state;
    const isLoading = paymentState === AUTHORIZING_STATE || paymentState === FINALIZING_STATE;

    const loadingMessage =
      paymentState === AUTHORIZING_STATE
        ? getTranslatedString('bpms.retrievingPaymentFeePleaseWait')
        : getTranslatedString('bamboraCheckout.processingPleaseWait');

    return (
      <React.Fragment>
        <Dialog
          fullScreen={this.props.fullScreen}
          open={this.props.open}
          aria-labelledby="payment-dialog"
          classes={{ paper: 'form-dialog' }}
        >
          <StyledDialogContainer>
            <LoadingMessage isLoading={isLoading} message={loadingMessage} />
            <PaysafeDirectDebitPaymentForm
              isHidden={paymentState !== PAYMENT_FORM_STATE}
              outstandingBalance={this.props.outstandingBalance}
              handleSubmit={this.authorizePaymentFormSubmit}
              handleClose={this.props.toggleOpen}
              lockedAmount={this.props.lockedAmount}
              paymentHold={this.props.paymentHold}
              padDetailType={this.props.padDetailType}
              showFeeInfo={this.props.showFeeInfo}
            />
            {paymentState === CONFIRMATION_STATE && (
              <PaysafeTenantConfirmation
                handleClose={this.closeConfirmation}
                finalizePayment={this.finalizePayment}
                paymentWithFee={this.props.paymentWithFee}
              />
            )}
            {paymentState === SUCCESS_STATE && (
              <PaysafeSuccessfulPayment
                handleClose={this.closePaymentSuccess}
                transactionId={this.state.transactionId}
                paymentSuccessDetails={this.props.paymentSuccessDetails}
              />
            )}
            {paymentState === FAILURE_STATE && (
              <PaysafeFailure
                handleClose={this.closePaymentFailure}
                paymentAuthorizeError={this.props.paymentAuthorizeError}
                paymentError={this.props.paymentError}
              />
            )}
          </StyledDialogContainer>
        </Dialog>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => {
  const { paymentDetails } = state.tenantProfile;

  return {
    paymentWithFee: paymentDetails.paymentWithFee,
    paymentSuccessDetails: paymentDetails.paymentSuccessDetails,
    paymentAuthorizeError: paymentDetails.paymentAuthorizeError,
    paymentError: paymentDetails.paymentError,
    tenantOverview: state.dashboard.tenantOverview,
  };
};

PaysafeDirectDebitPayment = connect(mapStateToProps, {
  getTenantTodoList,
})(PaysafeDirectDebitPayment);

export default localize()(PaysafeDirectDebitPayment);

const LoadingMessage = ({ isLoading, message }) => {
  if (!isLoading) return null;

  return (
    <StyledLoadingContainer>
      <SyncLoader color={AccentColor()} size="16px" margin="auto" />
      <StyledLoadingP>{message}</StyledLoadingP>
    </StyledLoadingContainer>
  );
};
